import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  ActionFunction,
  LoaderFunction,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
  useSubmit,
} from "react-router-dom";

import { get, put, PutRequest } from "api";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import { readFormData } from "toolbox/ReadFormData";

const load = async (membershipId: string) => {
  const userMemberships = queryClient.fetchQuery(
    ["/platform-user-memberships"],
    () => get("/platform-user-memberships")
  );

  return waitObj({
    userMemberships,
    membershipId,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipCancelConfirmLoader: LoaderFunction = ({
  params: { membershipId },
}) => {
  if (!membershipId) return redirect("..");

  return load(membershipId);
};

export const walletMembershipCancelAction: ActionFunction = async ({
  request,
}) => {
  const data = await readFormData<
    PutRequest<"/platform-user-memberships/{platformUserMembershipId}/cancel-membership">
  >(request);

  await put(
    "/platform-user-memberships/{platformUserMembershipId}/cancel-membership",
    data
  );

  await queryClient.invalidateQueries({
    predicate: (query) =>
      ["/platform-user-memberships", "/platform-user-memberships/status"].some(
        (key) => query.queryKey[0] == key
      ),
  });

  return redirect("/wallet/memberships/manage");
};

export const WalletMembershipCancelConfirmPage = () => {
  const location = useLocation();

  const data = useLoaderData() as LoaderData;

  const navigate = useNavigate();
  const submit = useSubmit();

  const { data: userMemberships } = useQuery({
    queryKey: ["/platform-user-memberships"],
    queryFn: () => get("/platform-user-memberships"),
    initialData: data.userMemberships,
  });
  const membership = userMemberships.find(
    (m) => m.membership.id == data.membershipId
  );

  const membershipCancellationReasonId: string =
    location.state?.membershipCancellationReasonId;

  if (!membershipCancellationReasonId) {
    navigate(`/wallet/memberships/manage/${data.membershipId}`);
  }

  return (
    <div className="relative flex h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          CANCEL MY MEMBERSHIP
        </div>
      </TopNav>
      <div className="flex flex-col gap-y-7 bg-white px-7 pt-3 text-left">
        <div className="text-title">
          Your {membership?.membership.name} membership will cancel on{" "}
          {dayjs(membership?.expiration).format("MMM D, YYYY")}
        </div>
        <div className="flex flex-col gap-y-3">
          <div>
            You can continue to enjoy your membership benefits until your
            membership is canceled.
          </div>
          <div>
            If you change your mind, you can undo your cancellation before{" "}
            {dayjs(membership?.expiration).format("MMM D, YYYY")}.
          </div>
        </div>
      </div>
      <div className="grow" />
      <div className="flex flex-col gap-y-3 px-7 pb-5">
        <Button
          fullWidth
          style="primary"
          onClick={async () => {
            if (membership?.platformUserMembershipId) {
              submit(
                {
                  platformUserMembershipId: membership.platformUserMembershipId,
                  membershipCancellationReasonId,
                },
                { method: "put" }
              );
            }
          }}
        >
          Continue
        </Button>
        <Button
          fullWidth
          style="secondary"
          onClick={() => navigate("/wallet/memberships")}
        >
          Keep my membership
        </Button>
      </div>
    </div>
  );
};
